import '@assets/App.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BrowserRouter as Router, Route, Routes, useParams, Navigate as RouterNavigate, useSearchParams } from 'react-router-dom';
import LoginPage from '@features/auth/pages/Login';
import LandingPage from '@features/landing/pages/LandingPage';
import ProtectedRouteSimple from '@core/components/ProtectedRouteSimple';
import PublicRoute from '@core/components/PublicRoute';
import NotFound from '@core/pages/NotFound';
import ValidationErrorModal from '@core/components/ValidationErrorModal';
import SystemErrorModal from '@core/components/SystemErrorModal';
import RequireReportQueryParams from '@core/components/RequireReportQueryParams';

// Entity navigation
import EntitySelector from '@features/entity/components/EntitySelector';
import EntityLayout from '@features/entity/components/EntityLayout';

// Dashboards
import EntityDashboard from '@features/dashboard/pages/EntityDashboard';

// Church pages
import ChurchUsersPage from '@features/church/pages/ChurchUsersPage';
import ChurchUserRoleAssignmentsPage from '@features/church/pages/ChurchUserRoleAssignmentsPage';
import ChurchInvitesPage from '@features/church/pages/ChurchInvitesPage';
import { AssignedTemplatesPage } from '@features/report/template/pages/AssignedTemplatesPage';

// District pages
import DistrictUsersPage from '@features/district/pages/DistrictUsersPage';
import DistrictUserRoleAssignmentsPage from '@features/district/pages/DistrictUserRoleAssignmentsPage';
import DistrictInvitesPage from '@features/district/pages/DistrictInvitesPage';
import DistrictChurchesPage from '@features/district/pages/DistrictChurchesPage';

// Nation pages
import NationUsersPage from '@features/nation/pages/NationUsersPage';
import NationUserRoleAssignmentsPage from '@features/nation/pages/NationUserRoleAssignmentsPage';
import NationInvitesPage from '@features/nation/pages/NationInvitesPage';
import NationDistrictsPage from '@features/nation/pages/NationDistrictsPage';

// Reports
import ManageActivityEntry from '@features/report/activity-entry/pages/ManageActivityEntry';
import ActivitySubmissionsList from '@features/report/activity-entry/pages/ActivitySubmissionsList';
import DepartmentReportsList from '@features/report/department-report/pages/DepartmentReportsList';

// Nation admin/config
import ManageKpi from '@features/admin/kpi/pages/ManageKpi';
import ManageActivity from '@features/admin/activity/pages/ManageActivity';
import ManageFinanceCategories from '@features/admin/finance/category/pages/ManageFinanceCategories';
import ManageFinanceAllocationRules from '@features/admin/finance/allocation-rule/pages/ManageFinanceAllocationRules';
import ManageReportTemplates from '@features/report/template/pages/ManageReportTemplates';
import ManageReportTags from '@features/report/tag/pages/ManageReportTags';
import ManageReportSubGroups from '@features/report/subgroup/pages/ManageReportSubGroups';
import AllChurchesPage from '@features/church/pages/AllChurchesPage';

// Shared
import SettingsPage from '@features/settings/pages/SettingsPage';

// User
import PendingInvitationsPage from '@features/user/invitations/pages/PendingInvitationsPage';
import UserProfilePage from '@features/user/pages/UserProfilePage';

// Admin invitations
import ManageInvitations from '@features/admin/invitations/pages/ManageInvitations';

// Auth
import RegisterPage from '@features/auth/pages/Register';
import ForgotPasswordPage from '@features/auth/pages/ForgotPassword';
import ResetPasswordPage from '@features/auth/pages/ResetPassword';

// Report pages (scoped routes)
import AllDepartmentsPage from '@features/department/pages/AllDepartmentsPage';
import ChurchReportPage from '@features/church/pages/ChurchReportPage';
import DistrictReportPage from '@features/district/pages/DistrictReportPage';
import NationReportPage from '@features/nation/pages/NationReportPage';
import ChurchPastorReportPage from '@features/report/leadership/pages/ChurchPastorReportPage';
import PastorAggregatedReportPage from '@features/report/leadership/pages/PastorAggregatedReportPage';
import ChurchFinanceStatisticsPage from '@features/report/leadership/pages/ChurchFinanceStatisticsPage';
import FinanceStatisticsAggregatedPage from '@features/report/leadership/pages/FinanceStatisticsAggregatedPage';

function GuardedChurchReportView() {
  const { churchId } = useParams<{ churchId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/church/${churchId}/department-reports/all`}
      children={<ChurchReportPage />}
    />
  );
}

function GuardedNationReportView() {
  const { nationId } = useParams<{ nationId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/nation/${nationId}/department-reports/all`}
      children={<NationReportPage />}
    />
  );
}

function GuardedDistrictReportView() {
  const { districtId } = useParams<{ districtId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/district/${districtId}/department-reports/all`}
      children={<DistrictReportPage />}
    />
  );
}

function GuardedChurchPastorReportView() {
  const { churchId } = useParams<{ churchId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/church/${churchId}/leadership-reports/pastor/all`}
      children={<ChurchPastorReportPage />}
    />
  );
}

function GuardedChurchFinanceStatisticsView() {
  const { churchId } = useParams<{ churchId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/church/${churchId}/leadership-reports/finance-statistics/all`}
      children={<ChurchFinanceStatisticsPage />}
    />
  );
}

function GuardedNationPastorView() {
  const { nationId } = useParams<{ nationId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/nation/${nationId}/leadership-reports/pastor/all`}
      children={<PastorAggregatedReportPage />}
    />
  );
}

function GuardedDistrictPastorView() {
  const { districtId } = useParams<{ districtId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/district/${districtId}/leadership-reports/pastor/all`}
      children={<PastorAggregatedReportPage />}
    />
  );
}

function GuardedNationFinanceStatisticsView() {
  const { nationId } = useParams<{ nationId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/nation/${nationId}/leadership-reports/finance-statistics/all`}
      children={<FinanceStatisticsAggregatedPage />}
    />
  );
}

function GuardedDistrictFinanceStatisticsView() {
  const { districtId } = useParams<{ districtId: string }>();
  return (
    <RequireReportQueryParams
      fallbackPath={`/district/${districtId}/leadership-reports/finance-statistics/all`}
      children={<FinanceStatisticsAggregatedPage />}
    />
  );
}

function RedirectDepartmentReportsToTemplates() {
  const { nationId } = useParams<{ nationId: string }>();
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const targetPath = `/nation/${nationId}/department/templates${queryString ? `?${queryString}` : ''}`;
  return <RouterNavigate to={targetPath} replace />;
}

function RedirectChurchDepartmentsToTemplates() {
  const { churchId } = useParams<{ churchId: string }>();
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const targetPath = `/church/${churchId}/templates${queryString ? `?${queryString}` : ''}`;
  return <RouterNavigate to={targetPath} replace />;
}

function App() {
  return (
    <>
      <a href="#main" className="ntcog-skip-link">
        Skip to main content
      </a>
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Routes>
          {/* Public routes — redirect to /entities if already logged in */}
          <Route element={<PublicRoute />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          <Route element={<ProtectedRouteSimple />}>
            {/* Entity Selection — no sidebar */}
            <Route path="/entities" element={<EntitySelector />} />
          </Route>

          <Route element={<ProtectedRouteSimple />}>
            {/* All pages with sidebar + header layout */}
            <Route element={<EntityLayout />}>

              {/* ===== Personal pages ===== */}
              <Route path="/pending-invitations" element={<PendingInvitationsPage />} />
              <Route path="/user/profile" element={<UserProfilePage />} />
              <Route path="/invitation/manage-invitations" element={<ManageInvitations />} />

              <Route path="/nation/:nationId/department/templates" element={<ManageReportTemplates />} />
              <Route path="/nation/:nationId/department/reports" element={<RedirectDepartmentReportsToTemplates />} />

              {/* ===== Nation routes ===== */}
              <Route path="/nation/:nationId/dashboard" element={<EntityDashboard />} />
              <Route path="/nation/:nationId/reports/view" element={<GuardedNationReportView />} />
              <Route path="/nation/:nationId/reports/submit" element={<ManageActivityEntry />} />
              <Route path="/nation/:nationId/templates" element={<AssignedTemplatesPage />} />
              <Route path="/nation/:nationId/users" element={<NationUsersPage />} />
              <Route path="/nation/:nationId/user-role-assignments" element={<NationUserRoleAssignmentsPage />} />
              <Route path="/nation/:nationId/invites" element={<NationInvitesPage />} />
              <Route path="/nation/:nationId/districts" element={<NationDistrictsPage />} />
              <Route path="/nation/:nationId/churches" element={<AllChurchesPage />} />
              <Route path="/nation/:nationId/departments" element={<AllDepartmentsPage />} />
              <Route path="/nation/:nationId/kpi" element={<ManageKpi />} />
              <Route path="/nation/:nationId/tags" element={<ManageReportTags />} />
              <Route path="/nation/:nationId/subgroups" element={<ManageReportSubGroups />} />
              <Route path="/nation/:nationId/activities" element={<ManageActivity />} />
              <Route path="/nation/:nationId/finance/categories" element={<ManageFinanceCategories />} />
              <Route path="/nation/:nationId/finance/allocation-rules" element={<ManageFinanceAllocationRules />} />
              <Route path="/nation/:nationId/department-reports/all" element={<DepartmentReportsList />} />
              <Route path="/nation/:nationId/department-reports/draft" element={<DepartmentReportsList />} />
              <Route path="/nation/:nationId/department-reports/submitted" element={<DepartmentReportsList />} />
              <Route path="/nation/:nationId/department-reports/needs-attention" element={<DepartmentReportsList />} />
              <Route path="/nation/:nationId/department-reports/awaiting-approval" element={<DepartmentReportsList />} />
              <Route path="/nation/:nationId/department-reports/approved" element={<DepartmentReportsList />} />
              <Route path="/nation/:nationId/leadership-reports/pastor/view" element={<GuardedNationPastorView />} />
              <Route path="/nation/:nationId/leadership-reports/pastor/:status" element={<DepartmentReportsList leadershipType="PASTOR" />} />
              <Route path="/nation/:nationId/leadership-reports/finance-statistics/view" element={<GuardedNationFinanceStatisticsView />} />
              <Route path="/nation/:nationId/leadership-reports/finance-statistics/:status" element={<DepartmentReportsList leadershipType="GENERAL" />} />
              <Route path="/nation/:nationId/activity-submissions/all" element={<ActivitySubmissionsList />} />
              <Route path="/nation/:nationId/activity-submissions/draft" element={<ActivitySubmissionsList />} />
              <Route path="/nation/:nationId/activity-submissions/submitted" element={<ActivitySubmissionsList />} />
              <Route path="/nation/:nationId/activity-submissions/needs-attention" element={<ActivitySubmissionsList />} />
              <Route path="/nation/:nationId/settings" element={<SettingsPage />} />

              {/* ===== District routes ===== */}
              <Route path="/district/:districtId/dashboard" element={<EntityDashboard />} />
              <Route path="/district/:districtId/reports/view" element={<GuardedDistrictReportView />} />
              <Route path="/district/:districtId/reports/submit" element={<ManageActivityEntry />} />
              <Route path="/district/:districtId/templates" element={<AssignedTemplatesPage />} />
              <Route path="/district/:districtId/users" element={<DistrictUsersPage />} />
              <Route path="/district/:districtId/user-role-assignments" element={<DistrictUserRoleAssignmentsPage />} />
              <Route path="/district/:districtId/invites" element={<DistrictInvitesPage />} />
              <Route path="/district/:districtId/churches" element={<DistrictChurchesPage />} />
              <Route path="/district/:districtId/department-reports/all" element={<DepartmentReportsList />} />
              <Route path="/district/:districtId/department-reports/draft" element={<DepartmentReportsList />} />
              <Route path="/district/:districtId/department-reports/submitted" element={<DepartmentReportsList />} />
              <Route path="/district/:districtId/department-reports/needs-attention" element={<DepartmentReportsList />} />
              <Route path="/district/:districtId/department-reports/awaiting-approval" element={<DepartmentReportsList />} />
              <Route path="/district/:districtId/department-reports/approved" element={<DepartmentReportsList />} />
              <Route path="/district/:districtId/leadership-reports/pastor/view" element={<GuardedDistrictPastorView />} />
              <Route path="/district/:districtId/leadership-reports/pastor/:status" element={<DepartmentReportsList leadershipType="PASTOR" />} />
              <Route path="/district/:districtId/leadership-reports/finance-statistics/view" element={<GuardedDistrictFinanceStatisticsView />} />
              <Route path="/district/:districtId/leadership-reports/finance-statistics/:status" element={<DepartmentReportsList leadershipType="GENERAL" />} />
              <Route path="/district/:districtId/activity-submissions/all" element={<ActivitySubmissionsList />} />
              <Route path="/district/:districtId/activity-submissions/draft" element={<ActivitySubmissionsList />} />
              <Route path="/district/:districtId/activity-submissions/submitted" element={<ActivitySubmissionsList />} />
              <Route path="/district/:districtId/activity-submissions/needs-attention" element={<ActivitySubmissionsList />} />
              <Route path="/district/:districtId/settings" element={<SettingsPage />} />

              {/* ===== Church routes ===== */}
              <Route path="/church/:churchId/dashboard" element={<EntityDashboard />} />
              <Route path="/church/:churchId/reports/submit" element={<ManageActivityEntry />} />
              <Route path="/church/:churchId/reports/view" element={<GuardedChurchReportView />} />
              <Route path="/church/:churchId/users" element={<ChurchUsersPage />} />
              <Route path="/church/:churchId/user-role-assignments" element={<ChurchUserRoleAssignmentsPage />} />
              <Route path="/church/:churchId/invites" element={<ChurchInvitesPage />} />
              <Route path="/church/:churchId/templates" element={<AssignedTemplatesPage />} />
              <Route path="/church/:churchId/departments" element={<RedirectChurchDepartmentsToTemplates />} />
              <Route path="/church/:churchId/department-reports/all" element={<DepartmentReportsList />} />
              <Route path="/church/:churchId/department-reports/draft" element={<DepartmentReportsList />} />
              <Route path="/church/:churchId/department-reports/submitted" element={<DepartmentReportsList />} />
              <Route path="/church/:churchId/department-reports/needs-attention" element={<DepartmentReportsList />} />
              <Route path="/church/:churchId/department-reports/awaiting-approval" element={<DepartmentReportsList />} />
              <Route path="/church/:churchId/department-reports/approved" element={<DepartmentReportsList />} />
              <Route path="/church/:churchId/leadership-reports/pastor/view" element={<GuardedChurchPastorReportView />} />
              <Route path="/church/:churchId/leadership-reports/pastor/:status" element={<DepartmentReportsList leadershipType="PASTOR" />} />
              <Route path="/church/:churchId/leadership-reports/finance-statistics/view" element={<GuardedChurchFinanceStatisticsView />} />
              <Route path="/church/:churchId/leadership-reports/finance-statistics/:status" element={<DepartmentReportsList leadershipType="GENERAL" />} />
              <Route path="/church/:churchId/activity-submissions/all" element={<ActivitySubmissionsList />} />
              <Route path="/church/:churchId/activity-submissions/draft" element={<ActivitySubmissionsList />} />
              <Route path="/church/:churchId/activity-submissions/submitted" element={<ActivitySubmissionsList />} />
              <Route path="/church/:churchId/activity-submissions/needs-attention" element={<ActivitySubmissionsList />} />
              <Route path="/church/:churchId/settings" element={<SettingsPage />} />

            </Route>
          </Route>

          {/* Catch-all route for undefined paths */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>

      {/* Global Error Modals */}
      <ValidationErrorModal />
      <SystemErrorModal />

      {/* Global Toast System (success/info toasts) */}
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar={false}
        closeOnClick
        pauseOnFocusLoss
        pauseOnHover
        draggable
        theme="colored"
      />
    </>
  );
}

export default App;
